import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { 
  TrendingUp, 
  DollarSign, 
  Users, 
  Wallet, 
  Settings, 
  LogOut,
  ChevronLeft,
  ChevronRight,
  Package,
  PieChart,
  Bell,
  HelpCircle,
  Layout,
  Shield,
  Zap,
  Gift
} from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { useTranslation } from "react-i18next";

export default function Sidebar() {
  const { t } = useTranslation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();

  const menuItems = [
    { icon: <Layout size={20} />, label: t('sidebar.dashboard'), path: "/dashboard" },
    { icon: <TrendingUp size={20} />, label: t('sidebar.spot_trading'), path: "/spot" },
    { icon: <Zap size={20} />, label: t('sidebar.futures_contracts'), path: "/futures" },
    { icon: <Package size={20} />, label: t('sidebar.invest_packs'), path: "/packs" },
    { icon: <Gift size={20} />, label: t('sidebar.bonuses'), path: "/bonuses" },
    { icon: <Wallet size={20} />, label: t('sidebar.my_portfolio'), path: "/wallet" },
    { icon: <Users size={20} />, label: t('sidebar.my_network'), path: "/team" },
    { icon: <Settings size={20} />, label: t('sidebar.settings'), path: "/settings" },
  ];

  const adminMenuItem = { icon: <Shield size={20} />, label: t('sidebar.administration'), path: "/admin" };

  const handleLogout = () => {
    logout();
  };

  return (
    <aside className={`hidden md:flex flex-col bg-[#020617] border-r border-white/5 transition-all duration-500 ease-in-out relative z-30 ${isCollapsed ? 'w-24' : 'w-72'}`}>
      
      {/* Collapse Toggle */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="absolute -right-3 top-10 w-6 h-6 bg-indigo-600 rounded-full flex items-center justify-center text-white shadow-lg shadow-indigo-500/40 hover:bg-indigo-500 transition-colors z-40 border border-white/20"
      >
        {isCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
      </button>

      {/* Header Info */}
      <div className="p-8 pb-4">
        {!isCollapsed ? (
          <div className="flex items-center gap-2 mb-8">
            <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center text-white shadow-lg shadow-indigo-500/20">
              <Zap size={18} fill="currentColor" />
            </div>
            <span className="text-xl font-black font-outfit text-white tracking-tighter">NOVA<span className="text-slate-500">PRO</span></span>
          </div>
        ) : (
          <div className="w-10 h-10 bg-indigo-500 rounded-xl flex items-center justify-center text-white shadow-lg shadow-indigo-500/20 mb-8 mx-auto">
             <Zap size={20} fill="currentColor" />
          </div>
        )}
      </div>

      {/* Profile Section */}
      <div className={`px-4 mb-8 transition-all duration-300 ${isCollapsed ? 'opacity-0 scale-95 pointer-events-none h-0' : 'opacity-100 scale-100 h-auto'}`}>
        <div className="p-4 rounded-2xl bg-white/[0.03] border border-white/5 relative overflow-hidden group">
          <div className="absolute inset-0 bg-indigo-500/5 translate-y-full group-hover:translate-y-0 transition-transform duration-500"></div>
          <div className="flex items-center gap-3 relative z-10">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-indigo-500 to-cyan-400 flex items-center justify-center text-white font-black text-xs shadow-lg">
              {user?.username?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-black text-white truncate uppercase tracking-widest">{user?.username || 'Trader'}</p>
              <div className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)] animate-pulse"></div>
                <p className="text-[9px] text-slate-500 font-bold uppercase tracking-widest">{user?.role || 'Member'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Menu */}
      <div className="p-4 mb-4">
          <p className={`text-[10px] font-black text-slate-600 uppercase tracking-[0.3em] mb-4 pl-4 ${isCollapsed ? 'text-center pl-0' : ''}`}>
            {isCollapsed ? '•••' : t('sidebar.navigation')}
          </p>
          <nav className="space-y-1">
            {[...menuItems, ...(user?.role === 'admin' ? [adminMenuItem] : [])].map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.label}
                  to={item.path}
                  className={`group flex items-center gap-4 px-4 py-3.5 rounded-xl transition-all duration-300 relative overflow-hidden ${
                    isActive
                      ? 'text-white'
                      : 'text-slate-500 hover:text-white'
                  }`}
                >
                  {isActive && (
                    <div className="absolute inset-0 bg-indigo-500/10 rounded-xl -z-10 animate-fade-in"></div>
                  )}
                  {isActive && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-indigo-500 rounded-r-full shadow-[0_0_12px_rgba(99,102,241,0.5)]"></div>
                  )}
                  
                  <div className={`flex-shrink-0 transition-transform duration-300 ${isActive ? 'text-indigo-400 scale-110' : 'group-hover:scale-110'}`}>
                    {item.icon}
                  </div>
                  {!isCollapsed && <span className="font-black text-[10px] uppercase tracking-[0.15em]">{item.label}</span>}
                </Link>
              );
            })}
          </nav>
      </div>

      {/* Footer Actions */}
      <div className="mt-auto p-4 border-t border-white/5 space-y-1">
        <Link 
          to="/support"
          className={`flex items-center gap-4 px-4 py-3.5 rounded-xl transition-all ${
            isCollapsed 
              ? 'justify-center bg-indigo-600/20 text-indigo-400' 
              : 'bg-indigo-600/10 border border-indigo-500/20 text-white hover:bg-indigo-600/20'
          }`}
        >
          <HelpCircle size={20} className={isCollapsed ? "text-indigo-400" : "text-indigo-400"} />
          {!isCollapsed && <span className="font-black text-[10px] uppercase tracking-[0.15em]">{t('sidebar.client_support')}</span>}
        </Link>
        <button
          onClick={handleLogout}
          className={`flex items-center gap-4 w-full px-4 py-3.5 text-rose-500/70 hover:bg-rose-500/10 hover:text-rose-400 rounded-xl transition-all ${isCollapsed ? 'justify-center' : ''}`}
        >
          <LogOut size={20} />
          {!isCollapsed && <span className="font-black text-[10px] uppercase tracking-[0.15em]">{t('sidebar.logout')}</span>}
        </button>
      </div>
    </aside>
  );
}